import React, { useRef, useEffect, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { cleanPastedHtml, isMarkdown, markdownToHtml } from '../../utils/pasteUtils';
import styles from './RichTextEditor.module.scss';

// Remap strike to <del> matching backend bleach allowlist
const Strike = Quill.import('formats/strike');
Strike.tagName = 'DEL';
Quill.register(Strike, true);

// Register highlight backed by <mark>
const Inline = Quill.import('blots/inline');
class HighlightBlot extends Inline {}
HighlightBlot.blotName = 'highlight';
HighlightBlot.tagName = 'mark';
Quill.register(HighlightBlot, true);

const icons = Quill.import('ui/icons');
icons.highlight = '<svg viewBox="0 0 18 18"><rect class="ql-fill" height="4" width="10" x="4" y="12" fill="#f59e0b" stroke="none"/><path class="ql-stroke" d="M6 12 L9 3 L11 3 L13 12" fill="none"/></svg>';

// Custom SmartClipboard that normalizes external HTML (ChatGPT, Gemini, Word, etc.)
// and converts Markdown from AI copy buttons into rich formatting
const BaseClipboard = Quill.import('modules/clipboard');

class SmartClipboard extends BaseClipboard {
  onPaste(e) {
    if (e.defaultPrevented || !this.quill.isEnabled()) return;

    const clipboardData = e.clipboardData || window.clipboardData;
    if (!clipboardData) {
      super.onPaste(e);
      return;
    }

    // Handle image attachments (screenshots) if callback provided
    const items = Array.from(clipboardData.items || []);
    const hasImage = items.some(item => item.type && item.type.startsWith('image/'));
    if (hasImage && this.options.onImagePaste) {
      this.options.onImagePaste(e);
      const hasText = clipboardData.getData('text/html') || clipboardData.getData('text/plain');
      if (!hasText) return;
    }

    const html = clipboardData.getData('text/html');
    const text = clipboardData.getData('text/plain');

    // 1. Rich HTML content (ChatGPT selection, Gemini, Word, Google Docs)
    if (html && html.trim()) {
      e.preventDefault();
      const cleaned = cleanPastedHtml(html);
      const range = this.quill.getSelection(true) || { index: this.quill.getLength(), length: 0 };
      if (range.length > 0) {
        this.quill.deleteText(range.index, range.length, Quill.sources.USER);
      }
      this.dangerouslyPasteHTML(range.index, cleaned, Quill.sources.USER);
      return;
    }

    // 2. Markdown text (e.g. from ChatGPT/Gemini "Copy" response button)
    if (text && text.trim() && isMarkdown(text)) {
      e.preventDefault();
      const converted = markdownToHtml(text);
      const range = this.quill.getSelection(true) || { index: this.quill.getLength(), length: 0 };
      if (range.length > 0) {
        this.quill.deleteText(range.index, range.length, Quill.sources.USER);
      }
      this.dangerouslyPasteHTML(range.index, converted, Quill.sources.USER);
      return;
    }

    // 3. Normal plain text paste
    super.onPaste(e);
  }
}

Quill.register('modules/clipboard', SmartClipboard, true);

const TOOLBAR_OPTIONS = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ script: 'sub' }, { script: 'super' }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  ['blockquote', 'code-block'],
  ['link', 'highlight'],
  ['clean'],
];

const FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike', 'script',
  'list', 'bullet', 'indent', 'blockquote', 'code-block', 'link', 'highlight',
];

const RichTextEditor = ({ value, onChange, placeholder, className, onPaste }) => {
  const quillRef = useRef(null);

  const modules = useMemo(() => ({
    toolbar: TOOLBAR_OPTIONS,
    clipboard: {
      matchVisual: false,
      onImagePaste: onPaste,
    },
  }), [onPaste]);

  useEffect(() => {
    if (!onPaste || !quillRef.current) return;
    const editor = quillRef.current.getEditor();
    const root = editor?.root;
    if (!root) return;

    const handleNativePaste = (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const hasImage = items.some(item => item.type && item.type.startsWith('image/'));
      if (hasImage) {
        onPaste(e);
      }
    };

    root.addEventListener('paste', handleNativePaste);
    return () => root.removeEventListener('paste', handleNativePaste);
  }, [onPaste]);

  return (
    <div className={`${styles.editor} ${className || ''}`}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        modules={modules}
        formats={FORMATS}
      />
    </div>
  );
};

export default RichTextEditor;
